'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { FreeMode } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import GameCard from '@/components/entities/games/GameCard';
import GameSkeleton from '@/components/entities/games/GameCard/components/GameSkeleton';
import { ShowAllButton } from '@/components/shared';
import { TOP_GAMES_CATEGORY_ID } from '@/consts/topGamesCategory';
import { AppRoutes } from '@/i18n/constants';
import { gameQueryOptions } from '@/queries/games/games.queries';
import 'swiper/css';
import 'swiper/css/free-mode';

const swiperProps = {
  slidesPerView: 'auto' as const,
  freeMode: true,
  watchOverflow: true,
  modules: [FreeMode],
  slidesOffsetBefore: 24,
  slidesOffsetAfter: 26,
  spaceBetween: 17,
};

const TopGamesSwiper = () => {
  const t = useTranslations('lobby.filters');

  const { data: popularGames, isLoading: isLoadingPopularGames } = useQuery(gameQueryOptions.list({
    params: {
      page: 1,
      limit: 9,
      filters: { categoryId: TOP_GAMES_CATEGORY_ID },
    },
  }));

  if (popularGames?.items.length === 0) {
    return null;
  }

  return (
    <>
      <p className="text-light mb-3 font-medium text-lg leading-5.5">
        {t('top-games')}
      </p>

      <div className="-mx-6 overflow-x-hidden overflow-y-visible hide-scrollbar relative shrink-0">
        <>
          {isLoadingPopularGames
            ? (
                <div className="flex pl-[1.3438rem] gap-2 px-2 w-full mt-2">
                  {Array.from({ length: 8 }).map((_, index) => (
                    <GameSkeleton key={index} className="p-0 bg-c950 w-30 rounded-xl" />
                  ))}
                </div>
              )
            : (
                <Swiper {...swiperProps} className="overflow-visible!">
                  {popularGames?.items.map(game => (
                    <SwiperSlide key={game.id} className="w-30! relative">
                      <GameCard
                        id={game.id}
                        likeButtonIconSize="compactLg"
                        likeButtonPosition="compactLg"
                        name={game.name}
                        cover={game.cover}
                        variant="game-cover-small"
                        path={AppRoutes.gamesId(game.id)}
                      />
                    </SwiperSlide>
                  ))}

                  <SwiperSlide
                    key={`show-all-${TOP_GAMES_CATEGORY_ID}`}
                    className="w-30! py-1 cursor-pointer flex items-center justify-center"
                  >
                    <ShowAllButton href={AppRoutes.categoryIdPaged(TOP_GAMES_CATEGORY_ID)} />
                  </SwiperSlide>
                </Swiper>
              )}
        </>
      </div>
    </>
  );
};

export default TopGamesSwiper;
